/* global React, OpenSeadragon, Icon */

const { useState, useEffect, useRef } = React;

// ── Map definitions (fixed order matches existing viewer) ────────────────────
const FIXED_REGIONS = [
  { id: 'CR', label: 'Costa Rica',               dziName: 'Costa Rica Map.dzi' },
  { id: 'PJ', label: 'Jacó Beach',               dziName: 'Jaco.dzi' },
  { id: 'TA', label: 'Tamarindo',                dziName: 'Tamarindo.dzi' },
  { id: 'FA', label: 'Arenal & La Fortuna',      dziName: 'Arenal Volcano & La Fortuna.dzi' },
  { id: 'MA', label: 'Manuel Antonio & Quepos',  dziName: 'Manuel Antonio & Quepos.dzi' },
  { id: 'CH', label: 'Playas del Coco & Hermosa',dziName: 'Playas del Coco & Hermosa.dzi' },
  { id: 'DU', label: 'Costa Ballena',            dziName: 'Costa Ballena.dzi' },
  { id: 'HE', label: 'Herradura Beach',          dziName: 'Herradura.dzi' },
];

// ── Category definitions ─────────────────────────────────────────────────────
// id = internal catKey used in adapted records
const BASE_CATS = [
  { id: 'all',            label: 'All',       icon: 'general_icon.png' },
  { id: 'accom',          label: 'Stay',       icon: 'accomodation_icom.png' },
  { id: 'rest',           label: 'Eat',        icon: 'restaurante_icon.png' },
  { id: 'tour',           label: 'Tours',      icon: 'transport_icon.png' },
  { id: 'shop',           label: 'Shop',       icon: 'shopping_icon.png' },
  { id: 'poi-beach',      label: 'Beach',      icon: 'beach_icon.png' },
  { id: 'poi-park',       label: 'Parks',      icon: 'park_icon.png' },
  { id: 'poi-monument',   label: 'Monument',   icon: 'monument_icon.png' },
  { id: 'poi-museum',     label: 'Museum',     icon: 'museum_icon.png' },
  { id: 'poi-hospital',   label: 'Hospital',   icon: 'hospital_icom.png' },
  { id: 'poi-police',     label: 'Police',     icon: 'police_icon.png' },
  { id: 'poi-atm',        label: 'ATM',        icon: 'atm_icon.png' },
  { id: 'poi-info',       label: 'Info',       icon: 'info_icon.png' },
  { id: 'poi-church',     label: 'Church',     icon: 'church_icon.png' },
  { id: 'poi-general',    label: 'Place',      icon: 'general_icon.png' },
];

const CAT_LABEL_I18N = {
  en: {
    all: 'All',
    accom: 'Stay',
    rest: 'Eat',
    tour: 'Tours',
    shop: 'Shop',
    'poi-beach': 'Beach',
    'poi-park': 'Parks',
    'poi-monument': 'Monument',
    'poi-museum': 'Museum',
    'poi-hospital': 'Hospital',
    'poi-police': 'Police',
    'poi-atm': 'ATM',
    'poi-info': 'Info',
    'poi-church': 'Church',
    'poi-general': 'Place',
  },
  es: {
    all: 'Todo',
    accom: 'Hospedaje',
    rest: 'Comer',
    tour: 'Tours',
    shop: 'Compras',
    'poi-beach': 'Playa',
    'poi-park': 'Parques',
    'poi-monument': 'Monumento',
    'poi-museum': 'Museo',
    'poi-hospital': 'Hospital',
    'poi-police': 'Policia',
    'poi-atm': 'ATM',
    'poi-info': 'Info',
    'poi-church': 'Iglesia',
    'poi-general': 'Lugar',
  },
};

function localizeCategoryLabel(catKey, fallback, lang) {
  return CAT_LABEL_I18N[lang]?.[catKey] || CAT_LABEL_I18N.en[catKey] || fallback || '';
}

// cat name shown in hotspot panel (HotspotPanel.jsx uses iconForCat with these names)
const CAT_DISPLAY = {
  accom:        'Accommodation',
  rest:         'Restaurant',
  tour:         'Tour',
  shop:         'Shop',
  'poi-beach':  'Beach',
  'poi-park':   'Park',
  'poi-promo':  'Promo',
  default:      'General',
};

const CAT_LABEL = {
  accom:        'Hotel / Resort',
  rest:         'Eat & Drink',
  tour:         'Tour & Activity',
  shop:         'Shopping',
  'poi-beach':  'Beach',
  'poi-park':   'National Park',
  'poi-promo':  'Link',
  default:      'Business',
};

const CAT_BADGE = {
  accom:           'Stay',
  rest:            'Eat',
  tour:            'Tours',
  shop:            'Shop',
  'poi-beach':     'Beach',
  'poi-park':      'Park',
  'poi-hospital':  'Hospital',
  'poi-police':    'Police',
  'poi-atm':       'ATM',
  'poi-info':      'Info',
  'poi-monument':  'Monument',
  'poi-museum':    'Museum',
  'poi-church':    'Church',
  'poi-general':   'Place',
  'poi-promo':     'Link',
  default:         'Place',
};

const CAT_ICON = {
  accom:          'accomodation_icom.png',
  rest:           'restaurante_icon.png',
  tour:           'transport_icon.png',
  shop:           'shopping_icon.png',
  'poi-beach':    'beach_icon.png',
  'poi-park':     'park_icon.png',
  'poi-hospital': 'hospital_icom.png',
  'poi-police':   'police_icon.png',
  'poi-atm':      'atm_icon.png',
  'poi-info':     'info_icon.png',
  'poi-monument': 'monument_icon.png',
  'poi-museum':   'museum_icon.png',
  'poi-church':   'church_icon.png',
  'poi-general':  'general_icon.png',
  'poi-promo':    'nav_icon.png',
  default:        'general_icon.png',
};

// ── Helpers (inline from hotspots.js, no module system here) ─────────────────
function firstVal(v) {
  return Array.isArray(v) ? (v[0] ?? '') : (v ?? '');
}

function normalizeRecords(payload) {
  const records = Array.isArray(payload)
    ? payload
    : (payload && typeof payload === 'object' && !payload.error ? Object.values(payload) : []);
  return records.filter(record => record && typeof record === 'object' && !Array.isArray(record));
}

function recordMergeKey(record) {
  const map = String(firstVal(record.Mapa) || '').trim().toUpperCase();
  const duplicateId = String(firstVal(record.__duplicateId) || '').trim().toLowerCase();
  const id = String(firstVal(record.id) || firstVal(record.ClientID) || '').trim();
  if (id) return duplicateId ? `${map}:${id}:dup:${duplicateId}` : `${map}:${id}`;

  const name = String(firstVal(record.Company) || '').trim().toLowerCase();
  const xy = String(firstVal(record.xy) || '').trim();
  return duplicateId ? `${map}:${name}:${xy}:dup:${duplicateId}` : `${map}:${name}:${xy}`;
}

function mergeUniqueRecords(primaryRecords, extraRecords) {
  const seen = new Set();
  const merged = [];
  [...primaryRecords, ...extraRecords].forEach(record => {
    const key = recordMergeKey(record);
    if (key && seen.has(key)) return;
    if (key) seen.add(key);
    merged.push(record);
  });
  return merged;
}

async function fetchJsonRecords(url) {
  try {
    const response = await fetch(url, { cache: 'no-cache' });
    if (!response.ok) return [];
    const payload = await response.json();
    return normalizeRecords(payload);
  } catch {
    return [];
  }
}

async function loadRegionRecords(regionId) {
  const code = String(regionId || '').trim().toUpperCase();
  if (!code) return [];

  const [hotspotRecords, extraRecords] = await Promise.all([
    fetchJsonRecords(`/api/hotspots/${code}.json`),
    fetchJsonRecords(`/api/poi-extras/${code}`),
  ]);

  return mergeUniqueRecords(hotspotRecords, extraRecords);
}

function isActive(v) {
  const s = String(firstVal(v) || '').trim().toUpperCase();
  return s === '1' || s === 'TRUE' || s === 'SI';
}

function getCatKey(record) {
  const rawPoi = String(firstVal(record.poiCategory) || '').trim().toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();

  const poiAliases = {
    hospital: 'hospital', clinic: 'hospital',
    policia: 'police', police: 'police',
    cajero: 'atm', banco: 'atm', atm: 'atm',
    parque: 'park', atraccion: 'park', park: 'park',
    playa: 'beach', playas: 'beach', beach: 'beach',
    info: 'info',
    monument: 'monument', monumento: 'monument', monumentos: 'monument',
    museo: 'museum', museum: 'museum', museos: 'museum',
    general: 'general',
    iglesia: 'church', church: 'church',
    promo: 'promo', promocion: 'promo', promociones: 'promo', link: 'promo',
  };

  const poiKey = poiAliases[rawPoi];
  if (poiKey) return `poi-${poiKey}`;

  if (isActive(record.Accom)) return 'accom';
  if (isActive(record.Rest))  return 'rest';
  if (isActive(record.Tour))  return 'tour';
  if (isActive(record.Shop))  return 'shop';
  return 'default';
}

function parseXY(rawXY) {
  const raw = String(rawXY || '').trim();
  if (!raw) return null;
  const m = raw.match(/-?\d+(?:\.\d+)?/g);
  if (!m || m.length < 2) return null;
  const x = parseFloat(m[0]);
  const y = parseFloat(m[1]);
  if (!isFinite(x) || !isFinite(y)) return null;
  const hasLabels = /\bX\s*:/i.test(raw) && /\bY\s*:/i.test(raw);
  return { x, y, preferredMode: hasLabels ? 'pixel' : null };
}

function detectXYMode(items) {
  if (!items.length) return 'pixel';
  if (items.some(p => p.x < 0 || p.y < 0)) return 'center-offset';
  const max = Math.max(...items.flatMap(p => [Math.abs(p.x), Math.abs(p.y)]));
  return max <= 1 ? 'normalized' : 'pixel';
}

function toImageCoordinates(parsedXY, imageWidth, imageHeight, mode) {
  if (!parsedXY) return null;

  if (parsedXY.preferredMode === 'pixel') {
    return { x: parsedXY.x, y: parsedXY.y };
  }

  if (mode === 'normalized') {
    return { x: parsedXY.x * imageWidth, y: parsedXY.y * imageHeight };
  }

  // coordenadas.html and poi-editor.html save plain "x, y" as center-offset.
  return { x: imageWidth / 2 + parsedXY.x, y: imageHeight / 2 + parsedXY.y };
}

function safeUrl(raw) {
  const v = String(firstVal(raw) || '').trim();
  if (!v || /^[.\-]$/.test(v) || /^N\/?A$/i.test(v)) return '';
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
}

function safePhone(raw) {
  const v = String(firstVal(raw) || '').trim();
  return (!v || /^[.\-]$/.test(v) || /^N\/?A$/i.test(v)) ? '' : v;
}

function safeEmail(raw) {
  const v = String(firstVal(raw) || '').trim().replace(/\s+/g, '');
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? v : '';
}

function safeTripAdvisor(raw) {
  const v = String(firstVal(raw) || '').trim();
  if (!v || v === '--' || /^[.\-]$/.test(v) || /^N\/?A$/i.test(v)) return '';
  if (/^https?:\/\//i.test(v)) return v;
  // Extract the full URL from the widget HTML's href attribute
  const m = v.match(/href="(https?:\/\/(?:www\.)?tripadvisor\.com\/[^"]+)"/i);
  if (m) return m[1];
  return '';
}

// Published ficha (server annotation, api/hotspots): only our own place paths.
function safePlaceUrls(value) {
  if (!value || typeof value !== 'object') return null;
  const es = String(value.es || '');
  const en = String(value.en || '');
  const ok = (path, prefix) => path.startsWith(prefix) && /^\/[a-z0-9._~%\/-]+\/$/i.test(path) && !path.includes('//');
  return ok(es, '/es/lugar/') && ok(en, '/en/place/') ? { es, en } : null;
}

// ── Record → template hotspot shape ─────────────────────────────────────────
function adaptRecord(record, index) {
  const catKey  = getCatKey(record);
  const icon    = CAT_ICON[catKey] || CAT_ICON.default;
  const xyParsed = parseXY(firstVal(record.xy));

  const phone    = safePhone(record.Tel1);
  const whatsapp = safePhone(record.whatsapp);
  const email    = safeEmail(record.email || record.Email);
  const website  = safeUrl(record.WebLink);
  const gmaps    = safeUrl(record.GmapLink);
  const igRaw        = safeUrl(record.instagram);
  const fbRaw        = safeUrl(record.fbLink);
  const tripadvisor  = safeTripAdvisor(record.tripadvisor);

  // Waze: always built from GPS coords; WazeLink legacy field is ignored
  const gps = String(firstVal(record.GPS) || '').trim();
  let waze = '';
  let gpsLat = null, gpsLon = null;
  if (gps) {
    const parts = gps.split(',');
    if (parts.length === 2 && parts.every(p => !isNaN(parseFloat(p.trim())))) {
      gpsLat = parseFloat(parts[0].trim());
      gpsLon = parseFloat(parts[1].trim());
      waze = `https://waze.com/ul?ll=${gpsLat},${gpsLon}&navigate=yes`;
    }
  }

  // Hero image: ArtworkUrl (external) → Artwork (local /imagenes/ads/)
  let hero = '';
  const artUrl = String(firstVal(record.ArtworkUrl) || '').trim();
  if (artUrl && /^https?:\/\//i.test(artUrl)) {
    // Strip own hosting domains so the image works from any domain.
    const own = artUrl.replace(
      /^https?:\/\/(www\.)?(staging\.crmaps\.com|crmaps\.com|localhost)(:\d+)?/i,
      ''
    );
    hero = own || artUrl;
  } else {
    const artFile = String(firstVal(record.Artwork) || '').trim();
    const validFile = artFile
      && artFile !== '--'
      && artFile !== '.'
      && !artFile.includes('/')
      && !artFile.includes('..')
      && /\.(jpe?g|png|webp|gif|avif)$/i.test(artFile);
    if (validFile) {
      // Legacy .jpg/.jpeg files were converted to .webp on the server.
      const resolvedFile = artFile.replace(/\.(jpe?g)$/i, '.webp');
      hero = `/imagenes/ads/${encodeURIComponent(resolvedFile)}`;
    }
  }

  const baseId = String(firstVal(record.ClientID) || firstVal(record.id) || `hs-${index}`);
  const duplicateId = String(firstVal(record.__duplicateId) || '').trim();

  return {
    id:       duplicateId ? `${baseId}::dup::${duplicateId}` : baseId,
    name:     String(firstVal(record.Company) || 'Unknown'),
    nameEn:   String(firstVal(record.CompanyEn || record.CompanyEN || record.Company_en || record.nameEn) || ''),
    cat:      CAT_DISPLAY[catKey] || CAT_DISPLAY.default,
    catBadge: CAT_BADGE[catKey]   || CAT_BADGE.default,
    catLabel: CAT_LABEL[catKey]   || CAT_LABEL.default,
    icon,
    hero,
    desc:     String(firstVal(record.description) || ''),
    descEn:   String(firstVal(record.descriptionEn || record.description_en || record.descriptionEnglish) || ''),
    contact: {
      phone,
      whatsapp,
      email,
      website,
      waze:      waze || '',
      gmaps:     gmaps || '',
      facebook:    fbRaw || '',
      instagram:   igRaw || '',
      tripadvisor: tripadvisor || '',
    },
    gpsLat,
    gpsLon,
    placeUrls: safePlaceUrls(record.placeUrls),
    // Private fields for OSD coordinate mapping and filtering
    _catKey:      catKey,
    _xyParsed:    xyParsed,
    _mapaId:      String(firstVal(record.Mapa) || '').trim(),
    switchMapId:  String(firstVal(record.switchMap) || '').trim().toUpperCase() || null,
  };
}

// Deep link (focusHotspot): a qué escala mostrar el mapa alrededor del pin, en px de
// pantalla por px del DZI (los 7 mapas miden 10630×7087). Se elige por escala y no por
// factor sobre el zoom de apertura para que el teléfono vea el mismo detalle que el
// escritorio: 0,42 es lo que da 4× en un visor de 1152px. Nunca menos de 2× la apertura.
const FOCUS_SCREEN_SCALE    = 0.42;
const FOCUS_ZOOM_MIN_FACTOR = 2;
// Deep link: ancho mínimo del visor (px) para abrir además el panel lateral. El panel
// mide hasta 430px pegado a la derecha; por debajo de este ancho taparía el pin recién
// centrado (en el teléfono ocupa el 94%), así que ahí sólo se acerca el mapa.
const FOCUS_PANEL_MIN_WIDTH = 900;

// ── Menús desplegables: cerrar al tocar afuera ───────────────────────────────
// Los selectores de mapa y de categorías (también los del directorio, que usa este
// hook) se cierran al tocar o hacer clic fuera de ellos. Ese toque sólo cierra: el
// click que lo sigue se descarta para que no abra por debajo un pin, una tarjeta o un
// enlace. Un arrastre sí pasa: el mapa o la lista se mueven igual. Los toques dentro
// de `refs` (cada botón con su lista) quedan a cargo de sus propios onClick.
function useDismissOnOutsideTap(open, refs, onDismiss) {
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event) => {
      if (refs.some(ref => ref.current && ref.current.contains(event.target))) return;
      onDismissRef.current();
      swallowNextClick();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
}

// Descarta el click del toque en curso. Si el gesto termina sin click (arrastre,
// scroll del navegador) se suelta al empezar el siguiente, o al segundo.
function swallowNextClick() {
  let timer = 0;
  const swallow = (event) => {
    event.preventDefault();
    event.stopPropagation();
    release();
  };
  const release = () => {
    clearTimeout(timer);
    window.removeEventListener('click', swallow, true);
    window.removeEventListener('pointerdown', release, true);
    window.removeEventListener('pointercancel', release, true);
  };
  window.addEventListener('click', swallow, true);
  window.addEventListener('pointerdown', release, true);
  window.addEventListener('pointercancel', release, true);
  timer = setTimeout(release, 1000);
}

// ── MapScreen ────────────────────────────────────────────────────────────────
function MapScreen({ onHotspotClick, activeHotspot, onTideClick, targetRegion, lang, onLanguageChange, copy, onMapChange, focusHotspot }) {
  // DOM refs
  const osdDivRef      = useRef(null);   // inner div passed to OSD
  const osdRef         = useRef(null);   // OSD viewer instance
  const overlayEls     = useRef([]);     // currently added overlay DOM elements
  const hotspotButtons = useRef([]);     // button nodes scaled with OSD zoom
  const hotspotBaseZoomRef = useRef(null); // zoom after fitVertically; pins start from here
  const isOpenRef      = useRef(false);  // true after OSD fires 'open'
  const slothyTimerRef = useRef(null);

  // Mutable refs — readable by OSD handlers without stale closures
  const hotspotsRef      = useRef([]);
  const catFilterRef     = useRef('all');
  const activeHotspotRef = useRef(null);
  const onClickRef       = useRef(onHotspotClick);
  // Deep link: id del hotspot pedido por la página (focusHotspot), el último ya aplicado
  // y el pin que lleva el anillo (is-focus) hasta que el visitante toque otro.
  const focusHotspotRef  = useRef(null);
  const focusDoneRef     = useRef(null);
  const focusedIdRef     = useRef(null);

  // State (drives re-renders / JSX)
  const [regions,       setRegions]       = useState([]);
  // Start on targetRegion when the host page locks the map (e.g. /en/jaco-beach/)
  const [selectedId,    setSelectedId]    = useState(targetRegion?.id || 'CR');
  const [hotspots,      setHotspots]      = useState([]);
  const [catFilter,     setCatFilter]     = useState('all');
  const [dropdownOpen,  setDropdownOpen]  = useState(false);
  const [catDropOpen,   setCatDropOpen]   = useState(false);
  const [loading,       setLoading]       = useState(false);
  const [slothyVisible, setSlothyVisible] = useState(false);

  // Keep refs in sync with latest values each render
  hotspotsRef.current      = hotspots;
  catFilterRef.current     = catFilter;
  activeHotspotRef.current = activeHotspot;
  onClickRef.current       = onHotspotClick;
  focusHotspotRef.current  = focusHotspot ? String(focusHotspot) : null;

  const selectedRegion = regions.find(r => r.id === selectedId) || regions[0];

  function showSlothyPeek(delay = 0) {
    if (slothyTimerRef.current) clearTimeout(slothyTimerRef.current);
    setSlothyVisible(false);
    slothyTimerRef.current = setTimeout(() => {
      setSlothyVisible(true);
      const hideTimer = setTimeout(() => {
        setSlothyVisible(false);
        slothyTimerRef.current = null;
      }, 3000);
      slothyTimerRef.current = hideTimer;
    }, delay);
  }

  // Switch to targetRegion when parent requests it (seq ensures re-fire on same id)
  useEffect(() => {
    if (!targetRegion?.id || !regions.length) return;
    const found = regions.find(r => r.id === targetRegion.id);
    if (found) setSelectedId(targetRegion.id);
  }, [targetRegion, regions]);

  // Only show categories that have at least one hotspot in current map
  const activeCats = BASE_CATS.filter(c =>
    c.id === 'all' || hotspots.some(h => h._catKey === c.id)
  );

  // ── purgeBadCoords — drops hotspots whose coords are outside image bounds ───
  function purgeBadCoords() {
    const viewer = osdRef.current;
    if (!viewer || !isOpenRef.current) return;
    const tiledImage = viewer.world.getItemAt(0);
    if (!tiledImage) return;
    const { x: imgW, y: imgH } = tiledImage.getContentSize();

    const hs = hotspotsRef.current;
    const parsed = hs.map(h => h._xyParsed).filter(Boolean);
    const mode = detectXYMode(parsed);

    const valid = hs.filter(h => {
      if (!h._xyParsed) return false;
      const point = toImageCoordinates(h._xyParsed, imgW, imgH, mode);
      return point && point.x >= 0 && point.y >= 0 && point.x <= imgW && point.y <= imgH;
    });

    if (valid.length !== hs.length) {
      hotspotsRef.current = valid;
      setHotspots(valid); // updates activeCats
    }
  }

  // ── syncOverlays — reads from refs, safe to call from OSD handlers ─────────
  function syncOverlays() {
    const viewer = osdRef.current;
    if (!viewer || !isOpenRef.current) return;

    // Clear previous overlays
    overlayEls.current.forEach(el => {
      try { viewer.removeOverlay(el); } catch (_) {}
    });
    overlayEls.current = [];
    hotspotButtons.current = [];

    const tiledImage = viewer.world.getItemAt(0);
    if (!tiledImage) return;

    const size = tiledImage.getContentSize();
    const imgW = size.x;
    const imgH = size.y;

    const hs     = hotspotsRef.current;
    const filter = catFilterRef.current;
    const activeId = activeHotspotRef.current;

    // Detect coordinate mode from all parsed XY values in this map
    const parsed = hs.map(h => h._xyParsed).filter(Boolean);
    const mode   = detectXYMode(parsed);

    const visible = filter === 'all' ? hs : hs.filter(h => h._catKey === filter);

    visible.forEach(h => {
      if (!h._xyParsed) return;

      const point = toImageCoordinates(h._xyParsed, imgW, imgH, mode);
      if (!point) return;

      // Skip if coordinates are out of image bounds
      if (point.x < 0 || point.y < 0 || point.x > imgW || point.y > imgH) return;

      const vpPoint = tiledImage.imageToViewportCoordinates(point.x, point.y);

      const el = document.createElement('div');
      el.className = 'hotspot-anchor';

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'hotspot-osd' + (activeId === h.id ? ' is-active' : '')
        + (focusedIdRef.current === h.id ? ' is-focus' : '');
      button.setAttribute('aria-label', h.name);
      button.dataset.hotspotId = h.id;
      button.innerHTML = `<img src="${window.CRMAPS_ASSET_BASE || ''}assets/icons/${h.icon}" alt="" />`;
      button.addEventListener('click', e => {
        e.stopPropagation();
        clearFocusRing();
        onClickRef.current(h);
      });
      el.appendChild(button);

      // Same convention as web.html/coordenadas.html/poi-editor.html: dot center sits at xy.
      viewer.addOverlay({
        element: el,
        location: vpPoint,
        placement: OpenSeadragon.Placement.CENTER,
      });
      overlayEls.current.push(el);
      hotspotButtons.current.push(button);
    });

    applyHotspotScale();
    applyPendingFocus();
  }

  // ── applyPendingFocus — deep link a un hotspot (2026-09-14) ─────────────────
  // Las páginas de destino pasan focusHotspot cuando la URL trae ?client=<id>
  // (la ficha comercial enlaza así desde «Cerca de acá»). Cuando el DZI está
  // abierto y los hotspots cargados, centra y acerca el mapa en ese pin y, si el
  // visor es ancho (FOCUS_PANEL_MIN_WIDTH), abre su panel como si se hubiera hecho
  // clic. Una sola vez por id: el visitante después navega libre. Un id que no
  // está en el mapa no hace nada.
  function applyPendingFocus() {
    const wanted = focusHotspotRef.current;
    if (!wanted || focusDoneRef.current === wanted) return;
    const viewer = osdRef.current;
    if (!viewer || !isOpenRef.current) return;
    const tiledImage = viewer.world.getItemAt(0);
    if (!tiledImage) return;
    const hs = hotspotsRef.current;
    if (!hs.length) return; // todavía no cargaron: se reintenta en el próximo syncOverlays

    focusDoneRef.current = wanted;
    const target = hs.find(h => String(h.id).split('::dup::')[0] === wanted);
    if (!target?._xyParsed) return;

    // Anillo sobre el pin pedido (app.css .is-focus): los botones ya están creados en
    // este syncOverlays, así que se marca el actual y el ref cubre los rebuilds siguientes.
    focusedIdRef.current = target.id;
    viewer.element?.querySelectorAll('.hotspot-osd').forEach(b => {
      b.classList.toggle('is-focus', b.dataset.hotspotId === String(target.id));
    });

    const size  = tiledImage.getContentSize();
    const mode  = detectXYMode(hs.map(h => h._xyParsed).filter(Boolean));
    const point = toImageCoordinates(target._xyParsed, size.x, size.y, mode);
    if (!point) return;
    const vpPoint = tiledImage.imageToViewportCoordinates(point.x, point.y);

    const baseZoom = Number.isFinite(hotspotBaseZoomRef.current) && hotspotBaseZoomRef.current > 0
      ? hotspotBaseZoomRef.current
      : viewer.viewport.getHomeZoom();
    const maxZoom = viewer.viewport.getMaxZoom();
    // Zoom OSD z: el visor muestra size.x / z px del mapa a lo ancho ⇒ escala = z · ancho / size.x.
    const viewerW = viewer.container?.clientWidth || viewer.element?.clientWidth || 0;
    const zoomPorEscala = viewerW > 0 ? FOCUS_SCREEN_SCALE * size.x / viewerW : baseZoom * FOCUS_ZOOM_MIN_FACTOR;
    const zoom = Math.min(maxZoom, Math.max(baseZoom * FOCUS_ZOOM_MIN_FACTOR, zoomPorEscala));
    const immediately = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true;
    const abrirPanel  = viewerW >= FOCUS_PANEL_MIN_WIDTH;

    // Después del refreshViewerLayout que el handler 'open' deja en cola.
    requestAnimationFrame(() => {
      if (osdRef.current !== viewer || !isOpenRef.current) return;
      // Sin refPoint en zoomTo: con uno, OSD corre el centro en cada cuadro para dejar
      // ese punto quieto en pantalla y el panTo termina descentrado (medido: 132px).
      viewer.viewport.panTo(vpPoint, immediately);
      viewer.viewport.zoomTo(zoom, null, immediately);
      viewer.viewport.applyConstraints(immediately);
      if (abrirPanel) onClickRef.current?.(target);
    });
  }

  // Quita el anillo del deep link (al tocar cualquier pin el visitante ya eligió).
  function clearFocusRing() {
    if (focusedIdRef.current === null) return;
    focusedIdRef.current = null;
    osdRef.current?.element?.querySelectorAll('.hotspot-osd.is-focus').forEach(b => b.classList.remove('is-focus'));
  }

  function computeHotspotScale() {
    const viewer = osdRef.current;
    if (!viewer?.viewport) return 1;

    const currentZoom = viewer.viewport.getZoom(true);
    const fallbackMinZoom = viewer.viewport.getHomeZoom();
    const baseZoom = Number.isFinite(hotspotBaseZoomRef.current) && hotspotBaseZoomRef.current > 0
      ? hotspotBaseZoomRef.current
      : fallbackMinZoom;
    const minZoom = Number.isFinite(baseZoom) && baseZoom > 0
      ? baseZoom
      : fallbackMinZoom;
    const maxZoom = Number.isFinite(viewer.viewport.maxZoomLevel) && viewer.viewport.maxZoomLevel > minZoom
      ? viewer.viewport.maxZoomLevel
      : (Number.isFinite(viewer.viewport.getMaxZoom?.()) ? viewer.viewport.getMaxZoom() : minZoom * 10);

    if (!Number.isFinite(currentZoom) || !Number.isFinite(minZoom) || !Number.isFinite(maxZoom) || minZoom <= 0 || maxZoom <= minZoom) {
      return 1;
    }

    const minScale = 0.30;
    const maxScale = 1.20;
    const logMin = Math.log(minZoom);
    const logMax = Math.log(maxZoom);
    const logCurrent = Math.log(Math.max(currentZoom, minZoom));
    const t = (logCurrent - logMin) / (logMax - logMin);
    const clampedT = Math.max(0, Math.min(1, t));

    return minScale + (maxScale - minScale) * clampedT;
  }

  function applyHotspotScale() {
    const scale = computeHotspotScale();
    hotspotButtons.current.forEach(button => {
      button.style.transform = `scale(${scale})`;
      button.style.transformOrigin = 'center center';
    });
  }

  function refreshViewerLayout(preserve = true) {
    const viewer = osdRef.current;
    if (!viewer?.viewport) return;

    const container = viewer.container || osdDivRef.current;
    const width = container?.clientWidth || osdDivRef.current?.clientWidth || 0;
    const height = container?.clientHeight || osdDivRef.current?.clientHeight || 0;
    if (!width || !height) return;

    viewer.viewport.resize(new OpenSeadragon.Point(width, height), preserve);
    if (window.CRMAPS_MAP_LOCK_MIN_ZOOM === true) {
      const homeZoom = viewer.viewport.getHomeZoom();
      if (Number.isFinite(homeZoom) && homeZoom > 0) viewer.viewport.minZoomLevel = homeZoom;
    }
    viewer.viewport.applyConstraints(true);
    viewer.forceRedraw?.();
    syncOverlays();
  }

  // ── Init OSD once on mount ────────────────────────────────────────────────
  useEffect(() => {
    if (!osdDivRef.current) return;

    const viewer = OpenSeadragon({
      element:               osdDivRef.current,
      showNavigationControl: false,
      showRotationControl:   false,
      gestureSettingsMouse:  { clickToZoom: false, scrollToZoom: true },
      gestureSettingsTouch:  { pinchToZoom: true, flickEnabled: true },
      animationTime:         0.3,
      springStiffness:       8,
      blendTime:             0.1,
      maxZoomLevel:          14,
      // Host pages (e.g. /en/jaco-beach/) can request home-style magnetic
      // edges via globals; the web-app/phone default stays 0.8 / false.
      visibilityRatio:       Number.isFinite(window.CRMAPS_MAP_VISIBILITY_RATIO) ? window.CRMAPS_MAP_VISIBILITY_RATIO : 0.8,
      constrainDuringPan:    window.CRMAPS_MAP_CONSTRAIN_PAN === true,
    });

    viewer.addHandler('open', () => {
      isOpenRef.current = true;
      viewer.viewport.fitVertically(true);
      hotspotBaseZoomRef.current = viewer.viewport.getZoom(true);
      // Host pages can lock zoom-out at the opening fit (like the home visor)
      // so the dark letterbox never shows; web-app/phone keep OSD defaults.
      if (window.CRMAPS_MAP_LOCK_MIN_ZOOM === true && Number.isFinite(hotspotBaseZoomRef.current) && hotspotBaseZoomRef.current > 0) {
        viewer.viewport.minZoomLevel = hotspotBaseZoomRef.current;
        viewer.viewport.applyConstraints(true);
      }
      requestAnimationFrame(() => refreshViewerLayout(true));
      purgeBadCoords();
      syncOverlays();
      showSlothyPeek(450);
    });

    viewer.addHandler('close', () => {
      isOpenRef.current = false;
    });

    viewer.addHandler('zoom', applyHotspotScale);
    viewer.addHandler('animation', applyHotspotScale);
    viewer.addHandler('animation-finish', applyHotspotScale);

    osdRef.current = viewer;

    const retargetCanvasClickToHotspot = (event) => {
      if (event.target?.closest?.('.hotspot-osd')) return;

      const hoveredHotspot = document
        .elementFromPoint(event.clientX, event.clientY)
        ?.closest?.('.hotspot-osd');

      if (!hoveredHotspot) return;

      event.preventDefault();
      event.stopPropagation();
      hoveredHotspot.click();
    };

    let resizeFrame = 0;
    const scheduleViewerResize = () => {
      if (resizeFrame) cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(() => {
        resizeFrame = 0;
        refreshViewerLayout(true);
      });
    };

    window.addEventListener('resize', scheduleViewerResize);
    window.visualViewport?.addEventListener('resize', scheduleViewerResize);
    window.visualViewport?.addEventListener('scroll', scheduleViewerResize);
    viewer.element?.addEventListener('click', retargetCanvasClickToHotspot, true);

    return () => {
      if (resizeFrame) cancelAnimationFrame(resizeFrame);
      window.removeEventListener('resize', scheduleViewerResize);
      window.visualViewport?.removeEventListener('resize', scheduleViewerResize);
      window.visualViewport?.removeEventListener('scroll', scheduleViewerResize);
      viewer.element?.removeEventListener('click', retargetCanvasClickToHotspot, true);
      if (slothyTimerRef.current) clearTimeout(slothyTimerRef.current);
      viewer.destroy();
      osdRef.current   = null;
      isOpenRef.current = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch available regions from manifest ─────────────────────────────────
  useEffect(() => {
    fetch('/tiles/manifest.json')
      .then(r => r.json())
      .then(data => {
        const manifestNames = (data.files || []).map(f => f.name.toLowerCase());
        const available = FIXED_REGIONS.filter(r =>
          manifestNames.includes(r.dziName.toLowerCase())
        );
        setRegions(available.length ? available : FIXED_REGIONS);
      })
      .catch(() => setRegions(FIXED_REGIONS));
  }, []);

  // ── Load map + hotspots when region changes ───────────────────────────────
  useEffect(() => {
    const region = regions.find(r => r.id === selectedId);
    if (!region || !osdRef.current) return;

    // Reset state before loading
    isOpenRef.current = false;
    hotspotBaseZoomRef.current = null;
    hotspotsRef.current = [];
    setHotspots([]);
    setLoading(true);
    overlayEls.current.forEach(el => {
      try { osdRef.current.removeOverlay(el); } catch (_) {}
    });
    overlayEls.current = [];
    hotspotButtons.current = [];

    // Encode only spaces — Vite's static server fails to resolve %26 for &
    osdRef.current.open(`/tiles/${region.dziName.replace(/ /g, '%20')}`);

    // Fetch the same merged data set as web.html: mapscr/proxy records + local POI extras.
    loadRegionRecords(region.id)
      .then(records => {
        const mapped  = records
          .filter(r => firstVal(r.xy)) // only records with coordinates
          .map((r, i) => ({ ...adaptRecord(r, i), region: region.id }));

        hotspotsRef.current = mapped;
        setHotspots(mapped);
        setLoading(false);

        // If OSD already opened, purge out-of-bounds coords then draw overlays
        if (isOpenRef.current) { purgeBadCoords(); syncOverlays(); }
        // Otherwise the 'open' handler will call syncOverlays when image loads
      })
      .catch(() => setLoading(false));
  }, [selectedId, regions]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Re-sync overlays when filter or active hotspot changes ───────────────
  useEffect(() => {
    syncOverlays();
  }, [catFilter, activeHotspot]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Close the map/category menus on a tap outside them ────────────────────
  const selectorWrapRef = useRef(null);
  const catsWrapRef     = useRef(null);
  useDismissOnOutsideTap(dropdownOpen || catDropOpen, [selectorWrapRef, catsWrapRef], () => {
    setDropdownOpen(false);
    setCatDropOpen(false);
  });

  // ── Handlers ──────────────────────────────────────────────────────────────
  function handleSelectMap(id) {
    setSelectedId(id);
    setDropdownOpen(false);
    setCatFilter('all');
    catFilterRef.current = 'all';
    onMapChange?.(id);
  }

  function handleSelectCat(id) {
    setCatFilter(id);
    catFilterRef.current = id;
    setCatDropOpen(false);
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="map-canvas">

      {/* OSD viewer — wrapper keeps position:absolute, inner div is owned by OSD */}
      <div className="map-osd-container">
        <div ref={osdDivRef} />
      </div>

      {/* Vignette overlay (pointer-events: none) */}
      <div className="map-overlay" />

      {/* ── Top chrome ── */}
      <div className="map-topchrome">
        {/* Map selector */}
        <div className="map-selector-wrap" ref={selectorWrapRef}>
          <button
            className="map-search map-selector"
            aria-haspopup="listbox"
            aria-expanded={dropdownOpen}
            onClick={() => { setDropdownOpen(o => !o); setCatDropOpen(false); }}
          >
            {Icon.search}
            <span className="ms-label">{selectedRegion?.label || copy?.selectMap || 'Select map'}</span>
            <span className={'ms-chevron' + (dropdownOpen ? ' is-open' : '')}>{Icon.chevDown}</span>
          </button>

          {dropdownOpen && (
            <div className="map-dropdown" role="listbox" aria-label="Map selector">
              {regions.map(r => (
                <button
                  key={r.id}
                  role="option"
                  aria-selected={r.id === selectedId}
                  className={'map-dropdown-item' + (r.id === selectedId ? ' is-selected' : '')}
                  onClick={() => handleSelectMap(r.id)}
                >
                  <span>{r.label}</span>
                  {r.id === selectedId && <span className="mdi-check">✓</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Category filter */}
        <div className="map-cats-wrap" ref={catsWrapRef}>
          <button
            className="map-iconbtn"
            aria-label={copy?.filterCategory || 'Filter by category'}
            aria-haspopup="listbox"
            aria-expanded={catDropOpen}
            onClick={() => { setCatDropOpen(o => !o); setDropdownOpen(false); }}
          >
            {Icon.layers}
          </button>

          {catDropOpen && (
            <div className="map-cats-dropdown" role="listbox" aria-label="Category selector">
              {activeCats.map(cat => (
                <button
                  key={cat.id}
                  role="option"
                  aria-selected={cat.id === catFilter}
                  className={'map-cats-item' + (cat.id === catFilter ? ' is-selected' : '')}
                  onClick={() => handleSelectCat(cat.id)}
                >
                  <span className="mci-left">
                    <img src={`${window.CRMAPS_ASSET_BASE || ''}assets/icons/${cat.icon}`} alt="" />
                    <span>{localizeCategoryLabel(cat.id, cat.label, lang)}</span>
                  </span>
                  {cat.id === catFilter && <span className="mdi-check">✓</span>}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="app-lang-toggle" aria-label="Language selector">
          <button type="button" className={lang === 'es' ? 'is-on' : ''} onClick={() => onLanguageChange?.('es')} aria-pressed={lang === 'es'}>ES</button>
          <button type="button" className={lang === 'en' ? 'is-on' : ''} onClick={() => onLanguageChange?.('en')} aria-pressed={lang === 'en'}>EN</button>
        </div>
      </div>

      {/* ── Bottom-right FAB controls ── */}
      <div className="map-bottom-fab">
        <button
          className="map-iconbtn"
          aria-label="Zoom in"
          onClick={() => osdRef.current?.viewport?.zoomBy(1.5, null, true)}
        >{Icon.plus}</button>
        <button
          className="map-iconbtn"
          aria-label="Zoom out"
          onClick={() => osdRef.current?.viewport?.zoomBy(0.67, null, true)}
        >{Icon.minus}</button>
      </div>

      {/* Loading indicator */}
      {loading && (
        <div style={{
          position: 'absolute', bottom: 110, left: '50%', transform: 'translateX(-50%)',
          background: 'rgba(0,0,0,0.65)', color: '#fff',
          padding: '6px 16px', borderRadius: 20,
          fontSize: 12, fontWeight: 600, letterSpacing: '0.04em',
          pointerEvents: 'none', zIndex: 10,
        }}>
          {copy?.loadingHotspots || 'Loading hotspots...'}
        </div>
      )}

      <div className={'map-slothy-peek' + (slothyVisible ? ' is-visible' : '')} aria-hidden="true">
        <img src={`${window.CRMAPS_ASSET_BASE || ''}assets/brand/slothy_3D.png`} alt="" />
      </div>

    </div>
  );
}

Object.assign(window, { MapScreen, BASE_CATS, FIXED_REGIONS, adaptRecord, loadRegionRecords, localizeCategoryLabel });
